import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { metricsAPI, contractsAPI, alertsAPI } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Link, Navigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  LineChart, Line, ResponsiveContainer
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Wallet,
  Receipt,
  PiggyBank,
  Calendar,
  Users,
  FileText,
  ArrowRight,
  RefreshCw,
  Banknote,
  Truck,
  UserCheck,
  Clock,
  Percent,
  Activity,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { cn } from "../lib/utils";

export default function Dashboard() {
  const { user, isAdmin, isLogistica, isVendedor } = useAuth();
  const [financialData, setFinancialData] = useState(null);
  const [operationalData, setOperationalData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [recentContracts, setRecentContracts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const isCurrentPeriod = selectedMonth === (now.getMonth() + 1) && selectedYear === now.getFullYear();

  const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    if (newMonth < 1) { newMonth = 12; newYear--; }
    if (newMonth > 12) { newMonth = 1; newYear++; }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const PeriodSelector = () => (
    <div className="flex items-center gap-2" data-testid="period-selector">
      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateMonth(-1)} data-testid="period-prev">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="flex items-center gap-1.5">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="period-month-select"
        >
          {MONTH_NAMES.map((name, i) => (
            <option key={i + 1} value={i + 1}>{name}</option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="h-8 px-2 rounded-md border border-slate-200 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="period-year-select"
        >
          {Array.from({ length: now.getFullYear() - 2023 }, (_, i) => 2024 + i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateMonth(1)} data-testid="period-next">
        <ChevronRight className="w-4 h-4" />
      </Button>
      {!isCurrentPeriod && (
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-emerald-600 hover:text-emerald-700"
          onClick={() => { setSelectedMonth(now.getMonth() + 1); setSelectedYear(now.getFullYear()); }}
          data-testid="period-reset"
        >
          Hoy
        </Button>
      )}
    </div>
  );

  const PeriodBadge = () => {
    if (isCurrentPeriod) return null;
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const comparisons = financialData?.comparisons || operationalData?.services_comparison;
    const hasComparisons = financialData?.comparisons;
    return (
      <div className="flex flex-col gap-1" data-testid="period-badge">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
          <Calendar className="w-3 h-3" />
          Viendo: {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
        </div>
        {hasComparisons && (
          <div className="text-[11px] text-slate-500 pl-1" data-testid="period-comparison-summary">
            vs {MONTH_NAMES[prevMonth - 1]}:
            {" Ingresos "}
            <span className={hasComparisons.income?.delta >= 0 ? "text-emerald-600 font-medium" : "text-red-500 font-medium"}>
              {hasComparisons.income?.delta >= 0 ? "+" : ""}{(hasComparisons.income?.delta || 0).toLocaleString("es-MX", {style: "currency", currency: "MXN", maximumFractionDigits: 0})}
            </span>
            {" · Servicios "}
            <span className={hasComparisons.services?.delta >= 0 ? "text-emerald-600 font-medium" : "text-red-500 font-medium"}>
              {hasComparisons.services?.delta >= 0 ? "+" : ""}{hasComparisons.services?.delta || 0}
            </span>
          </div>
        )}
        {!hasComparisons && operationalData?.services_comparison && (
          <div className="text-[11px] text-slate-500 pl-1" data-testid="period-comparison-summary">
            vs {MONTH_NAMES[prevMonth - 1]}:
            {" Servicios "}
            <span className={operationalData.services_comparison.delta >= 0 ? "text-emerald-600 font-medium" : "text-red-500 font-medium"}>
              {operationalData.services_comparison.delta >= 0 ? "+" : ""}{operationalData.services_comparison.delta || 0}
              {operationalData.services_comparison.pct !== undefined ? ` (${operationalData.services_comparison.pct > 0 ? "+" : ""}${operationalData.services_comparison.pct}%)` : ""}
            </span>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchData();
  }, [isAdmin, isVendedor, isLogistica, selectedMonth, selectedYear]);

  const periodParams = { month: selectedMonth, year: selectedYear };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const [financialRes, operationalRes, contractsRes, alertsRes] = await Promise.all([
          metricsAPI.getFinancialDashboard(periodParams),
          metricsAPI.getDashboard(periodParams),
          contractsAPI.getAll({ limit: 5 }),
          api.get("/alerts/check", { params: periodParams }).catch(() => ({ data: { alerts: [] } }))
        ]);
        setFinancialData(financialRes.data);
        setOperationalData(operationalRes.data);
        setRecentContracts(contractsRes.data.slice(0, 5));
        setAlerts(alertsRes.data?.alerts || []);
      } else if (isVendedor) {
        const [metricsRes, contractsRes] = await Promise.all([
          metricsAPI.getDashboard(periodParams),
          contractsAPI.getAll({ limit: 5 })
        ]);
        setVendorData(metricsRes.data);
        setRecentContracts(contractsRes.data.slice(0, 5));
      } else if (isLogistica) {
        const [metricsRes, contractsRes, alertsRes] = await Promise.all([
          metricsAPI.getDashboard(periodParams),
          contractsAPI.getAll({ limit: 5 }),
          api.get("/alerts/check", { params: periodParams }).catch(() => ({ data: { alerts: [] } }))
        ]);
        setOperationalData(metricsRes.data);
        setRecentContracts(contractsRes.data.slice(0, 5));
        setAlerts(alertsRes.data?.alerts || []);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-slate-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-slate-500">Cargando dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ==================== ADMIN FINANCIAL DASHBOARD ====================
  if (isAdmin && financialData) {
    const { kpis, charts, comparisons } = financialData;
    
    // Helper for comparison badges
    const ComparisonBadge = ({ comparison, inverted = false }) => {
      if (!comparison) return null;
      const { delta, pct } = comparison;
      if (delta === 0 && pct === 0) return null;
      // For expenses, positive delta is bad (inverted)
      const isPositive = inverted ? delta < 0 : delta > 0;
      return (
        <div className={cn(
          "flex items-center gap-1 text-xs font-medium mt-1",
          isPositive ? "text-emerald-600" : "text-red-600"
        )}>
          {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{delta > 0 ? "+" : ""}{formatCurrency(delta)} ({pct > 0 ? "+" : ""}{pct}%)</span>
        </div>
      );
    };
    
    return (
      <Layout>
        <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-financial">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h1 className="text-3xl font-heading font-bold text-slate-900">Dashboard Financiero</h1>
                <p className="text-slate-500 mt-1">Control de cobranza, facturacion y utilidad</p>
              </div>
              <PeriodBadge />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <PeriodSelector />
              <Button variant="outline" onClick={fetchData} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </div>

          {/* ===== ALERT BANNER ===== */}
          {alerts.length > 0 && (
            <div className="space-y-2" data-testid="alerts-banner">
              {alerts.filter(a => a.severity === "critical").map((alert, i) => (
                <div key={`crit-${i}`} className="flex items-center gap-3 p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="alert-critical">
                  <div className="p-2 bg-red-200 rounded-full flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">{alert.message}</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      {alert.details?.slice(0, 3).map(d => d.folio).join(", ")}
                      {alert.count > 3 ? ` y ${alert.count - 3} mas...` : ""}
                    </p>
                  </div>
                </div>
              ))}
              {alerts.filter(a => a.severity === "warning").map((alert, i) => (
                <div key={`warn-${i}`} className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="alert-warning">
                  <div className="p-2 bg-amber-200 rounded-full flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">{alert.message}</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {alert.details?.slice(0, 3).map(d => `${d.folio} (falta ${d.missing})`).join(", ")}
                      {alert.count > 3 ? ` y ${alert.count - 3} mas...` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* KPI Cards - Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Ventas Contratadas del Mes */}
            <Card className="card-hover border-l-4 border-l-emerald-500" data-testid="kpi-sales-month">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Ventas Contratadas del Mes</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(kpis.sales_month)}</p>
                    <p className="text-xs text-slate-400 mt-1">Por fecha de alta del contrato</p>
                  </div>
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ventas Contratadas del Año */}
            <Card className="card-hover border-l-4 border-l-blue-500" data-testid="kpi-sales-year">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Ventas Contratadas del Año</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(kpis.sales_year)}</p>
                    <p className="text-xs text-slate-400 mt-1">Por fecha de alta, no facturación fiscal</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Por Cobrar del Año */}
            <Card className="card-hover border-l-4 border-l-amber-500" data-testid="kpi-pending-year">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Por Cobrar del Año</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(kpis.pending_to_collect_year)}</p>
                    {kpis.overdue_year > 0 && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {kpis.overdue_year} vencido{kpis.overdue_year !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Wallet className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Por Cobrar Histórico */}
            <Card className="card-hover border-l-4 border-l-orange-400" data-testid="kpi-pending-historical">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Por Cobrar Histórico</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(kpis.pending_to_collect_historical)}</p>
                    {kpis.overdue_historical > 0 && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {kpis.overdue_historical} vencido{kpis.overdue_historical !== 1 ? 's' : ''} total
                      </p>
                    )}
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Receipt className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vencidos del Año */}
            <Card className={cn(
              "card-hover border-l-4",
              kpis.overdue_year > 0 ? "border-l-red-500" : "border-l-green-500"
            )} data-testid="kpi-overdue-year">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Vencidos del Año</p>
                    <p className={cn(
                      "text-2xl font-bold mt-1",
                      kpis.overdue_year > 0 ? "text-red-600" : "text-green-600"
                    )}>
                      {kpis.overdue_year}
                    </p>
                  </div>
                  <div className={cn(
                    "p-2 rounded-lg",
                    kpis.overdue_year > 0 ? "bg-red-100" : "bg-green-100"
                  )}>
                    <AlertTriangle className={cn(
                      "w-5 h-5",
                      kpis.overdue_year > 0 ? "text-red-600" : "text-green-600"
                    )} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vencidos Históricos */}
            <Card className={cn(
              "card-hover border-l-4",
              kpis.overdue_historical > 0 ? "border-l-red-400" : "border-l-green-400"
            )} data-testid="kpi-overdue-historical">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Vencidos Históricos</p>
                    <p className={cn(
                      "text-2xl font-bold mt-1",
                      kpis.overdue_historical > 0 ? "text-red-500" : "text-green-500"
                    )}>
                      {kpis.overdue_historical}
                    </p>
                  </div>
                  <div className={cn(
                    "p-2 rounded-lg",
                    kpis.overdue_historical > 0 ? "bg-red-50" : "bg-green-50"
                  )}>
                    <AlertTriangle className={cn(
                      "w-5 h-5",
                      kpis.overdue_historical > 0 ? "text-red-500" : "text-green-500"
                    )} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* KPI Cards - Row 2 (Financial) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Cobrado del Mes */}
            <Card className="card-hover bg-gradient-to-br from-green-50 to-emerald-50" data-testid="kpi-income-month">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Cobrado del Mes</p>
                    <p className="text-3xl font-bold text-emerald-700 mt-2">{formatCurrency(kpis.income_month)}</p>
                  </div>
                  <div className="p-3 bg-emerald-200 rounded-xl">
                    <DollarSign className="w-6 h-6 text-emerald-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cobrado del Año */}
            <Card className="card-hover bg-gradient-to-br from-teal-50 to-cyan-50" data-testid="kpi-income-year">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-teal-700">Cobrado del Año</p>
                    <p className="text-3xl font-bold text-teal-700 mt-2">{formatCurrency(kpis.income_year)}</p>
                  </div>
                  <div className="p-3 bg-teal-200 rounded-xl">
                    <Banknote className="w-6 h-6 text-teal-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gastos del Mes */}
            <Card className="card-hover bg-gradient-to-br from-red-50 to-rose-50">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-rose-700">Gastos del Mes</p>
                    <p className="text-3xl font-bold text-rose-700 mt-2">{formatCurrency(kpis.expenses_month)}</p>
                  </div>
                  <div className="p-3 bg-rose-200 rounded-xl">
                    <Receipt className="w-6 h-6 text-rose-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Utilidad Estimada */}
            <Card className={cn(
              "card-hover bg-gradient-to-br",
              kpis.profit_month >= 0 
                ? "from-blue-50 to-indigo-50" 
                : "from-orange-50 to-amber-50"
            )}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className={cn(
                      "text-sm font-medium",
                      kpis.profit_month >= 0 ? "text-indigo-700" : "text-amber-700"
                    )}>
                      Utilidad Estimada
                    </p>
                    <p className={cn(
                      "text-3xl font-bold mt-2",
                      kpis.profit_month >= 0 ? "text-indigo-700" : "text-amber-700"
                    )}>
                      {formatCurrency(kpis.profit_month)}
                    </p>
                  </div>
                  <div className={cn(
                    "p-3 rounded-xl",
                    kpis.profit_month >= 0 ? "bg-indigo-200" : "bg-amber-200"
                  )}>
                    <PiggyBank className={cn(
                      "w-6 h-6",
                      kpis.profit_month >= 0 ? "text-indigo-700" : "text-amber-700"
                    )} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ===== RENTABILIDAD DEL MES ===== */}
          <div className="mt-6" data-testid="monthly-profitability-section">
            <h3 className="text-base font-heading text-slate-600 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Rentabilidad del Mes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Ingresos del Mes (Ledger) */}
              <Card className="card-hover bg-gradient-to-br from-emerald-50 to-green-50" data-testid="income-ledger-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-emerald-700">Ingresos del Mes</p>
                      <p className="text-2xl font-bold text-emerald-700 mt-2">{formatCurrency(kpis.income_month)}</p>
                      <ComparisonBadge comparison={comparisons?.income} />
                      <p className="text-xs text-emerald-600 mt-1">Basado en ledger</p>
                    </div>
                    <div className="p-3 bg-emerald-200 rounded-xl">
                      <TrendingUp className="w-5 h-5 text-emerald-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Egresos del Mes (Ledger) */}
              <Card className="card-hover bg-gradient-to-br from-red-50 to-rose-50" data-testid="expenses-ledger-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-rose-700">Egresos del Mes</p>
                      <p className="text-2xl font-bold text-rose-700 mt-2">{formatCurrency(kpis.expenses_ledger_month)}</p>
                      <ComparisonBadge comparison={comparisons?.expenses} inverted />
                      <p className="text-xs text-rose-600 mt-1">Basado en ledger</p>
                    </div>
                    <div className="p-3 bg-rose-200 rounded-xl">
                      <TrendingDown className="w-5 h-5 text-rose-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Utilidad del Mes */}
              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (kpis.profit_ledger_month || 0) >= 0
                  ? "from-blue-50 to-indigo-50"
                  : "from-orange-50 to-amber-50"
              )} data-testid="profit-ledger-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (kpis.profit_ledger_month || 0) >= 0 ? "text-indigo-700" : "text-amber-700"
                      )}>Utilidad del Mes</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (kpis.profit_ledger_month || 0) >= 0 ? "text-indigo-700" : "text-amber-700"
                      )}>{formatCurrency(kpis.profit_ledger_month)}</p>
                      <ComparisonBadge comparison={comparisons?.profit} />
                      <p className="text-xs text-slate-500 mt-1">Ingresos - Egresos</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (kpis.profit_ledger_month || 0) >= 0 ? "bg-indigo-200" : "bg-amber-200"
                    )}>
                      <DollarSign className={cn(
                        "w-5 h-5",
                        (kpis.profit_ledger_month || 0) >= 0 ? "text-indigo-700" : "text-amber-700"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Margen del Mes */}
              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (kpis.margin_month || 0) >= 20
                  ? "from-violet-50 to-purple-50"
                  : (kpis.margin_month || 0) >= 0
                    ? "from-slate-50 to-gray-50"
                    : "from-orange-50 to-amber-50"
              )} data-testid="margin-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (kpis.margin_month || 0) >= 20 ? "text-violet-700" : "text-slate-700"
                      )}>Margen del Mes</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (kpis.margin_month || 0) >= 20 ? "text-violet-700" : "text-slate-700"
                      )}>{kpis.margin_month || 0}%</p>
                      <p className="text-xs text-slate-500 mt-1">Utilidad / Ingresos</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (kpis.margin_month || 0) >= 20 ? "bg-violet-200" : "bg-slate-200"
                    )}>
                      <Percent className={cn(
                        "w-5 h-5",
                        (kpis.margin_month || 0) >= 20 ? "text-violet-700" : "text-slate-700"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ===== OPERATIVO DEL MES ===== */}
          <div className="mt-6" data-testid="monthly-operations-section">
            <h3 className="text-base font-heading text-slate-600 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Operativo del Mes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Servicios del Mes */}
              <Card className="card-hover bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="services-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-sky-700">Servicios del Mes</p>
                      <p className="text-2xl font-bold text-sky-700 mt-2">{kpis.services_month || 0}</p>
                      <ComparisonBadge comparison={comparisons?.services} />
                      <p className="text-xs text-sky-600 mt-1">Activos (excl. completados)</p>
                    </div>
                    <div className="p-3 bg-sky-200 rounded-xl">
                      <Calendar className="w-5 h-5 text-sky-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Servicios Asignados */}
              <Card className="card-hover bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="services-assigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-teal-700">Asignados</p>
                      <p className="text-2xl font-bold text-teal-700 mt-2">{kpis.services_assigned || 0}</p>
                      <p className="text-xs text-teal-600 mt-1">Con vehiculo y chofer</p>
                    </div>
                    <div className="p-3 bg-teal-200 rounded-xl">
                      <UserCheck className="w-5 h-5 text-teal-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Servicios Sin Asignación */}
              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (kpis.services_unassigned || 0) > 0
                  ? "from-amber-50 to-yellow-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="services-unassigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (kpis.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>Sin Asignacion</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (kpis.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>{kpis.services_unassigned || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Falta vehiculo o chofer</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (kpis.services_unassigned || 0) > 0 ? "bg-amber-200" : "bg-slate-200"
                    )}>
                      <Truck className={cn(
                        "w-5 h-5",
                        (kpis.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Servicios Vencidos */}
              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (kpis.services_overdue || 0) > 0
                  ? "from-red-50 to-rose-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="services-overdue">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (kpis.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>Vencidos</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (kpis.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>{kpis.services_overdue || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Fecha pasada, no completados</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (kpis.services_overdue || 0) > 0 ? "bg-red-200" : "bg-slate-200"
                    )}>
                      <Clock className={cn(
                        "w-5 h-5",
                        (kpis.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ventas Contratadas por Vendedor - Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-500" />
                  Ventas Contratadas por Vendedor ({MONTH_NAMES[selectedMonth - 1]} {selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {charts.sales_by_vendor && charts.sales_by_vendor.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={charts.sales_by_vendor} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="sales" name="Ventas Contratadas" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    No hay datos de ventas contratadas por vendedor
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ventas Contratadas por Mes - Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-slate-500" />
                  Ventas Contratadas por Mes (12 meses)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {charts.sales_by_month && charts.sales_by_month.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={charts.sales_by_month} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="sales" 
                        name="Ventas Contratadas"
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    No hay datos de ventas contratadas mensuales
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/finanzas" className="block">
              <Card className="card-hover cursor-pointer transition-all hover:border-emerald-300">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium">Ir a Finanzas</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/contratos" className="block">
              <Card className="card-hover cursor-pointer transition-all hover:border-blue-300">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-medium">Ver Contratos</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/comisiones" className="block">
              <Card className="card-hover cursor-pointer transition-all hover:border-purple-300">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="font-medium">Ver Comisiones</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // ==================== VENDEDOR DASHBOARD ====================
  if (isVendedor && vendorData) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-vendedor">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h1 className="text-3xl font-heading font-bold text-slate-900">Mi Dashboard</h1>
                <p className="text-slate-500 mt-1">Bienvenido, {user?.name}</p>
              </div>
              <PeriodBadge />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <PeriodSelector />
              <Button variant="outline" onClick={fetchData} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Total Contratos</p>
                <p className="text-2xl font-bold text-slate-900">{vendorData.total_contracts || 0}</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Contratos Activos</p>
                <p className="text-2xl font-bold text-blue-600">{vendorData.pending_contracts || 0}</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Total Comisiones</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(vendorData.total_commission)}</p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Comisiones Pendientes</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(vendorData.pending_commission)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Operativo del Mes - Mis Servicios */}
          <div className="mt-6" data-testid="monthly-operations-section-vendedor">
            <h3 className="text-base font-heading text-slate-600 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Mis Servicios del Mes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="card-hover bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="vnd-services-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-sky-700">Servicios del Mes</p>
                      <p className="text-2xl font-bold text-sky-700 mt-2">{vendorData.services_month || 0}</p>
                    </div>
                    <div className="p-3 bg-sky-200 rounded-xl">
                      <Calendar className="w-5 h-5 text-sky-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="vnd-services-assigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-teal-700">Asignados</p>
                      <p className="text-2xl font-bold text-teal-700 mt-2">{vendorData.services_assigned || 0}</p>
                      <p className="text-xs text-teal-600 mt-1">Con vehiculo y chofer</p>
                    </div>
                    <div className="p-3 bg-teal-200 rounded-xl">
                      <UserCheck className="w-5 h-5 text-teal-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (vendorData.services_unassigned || 0) > 0
                  ? "from-amber-50 to-yellow-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="vnd-services-unassigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (vendorData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>Sin Asignacion</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (vendorData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>{vendorData.services_unassigned || 0}</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (vendorData.services_unassigned || 0) > 0 ? "bg-amber-200" : "bg-slate-200"
                    )}>
                      <Truck className={cn(
                        "w-5 h-5",
                        (vendorData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (vendorData.services_overdue || 0) > 0
                  ? "from-red-50 to-rose-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="vnd-services-overdue">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (vendorData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>Vencidos</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (vendorData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>{vendorData.services_overdue || 0}</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (vendorData.services_overdue || 0) > 0 ? "bg-red-200" : "bg-slate-200"
                    )}>
                      <Clock className={cn(
                        "w-5 h-5",
                        (vendorData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mis Contratos Recientes</CardTitle>
            </CardHeader>
            <CardContent>
              {recentContracts.length > 0 ? (
                <div className="space-y-3">
                  {recentContracts.map((contract) => (
                    <Link 
                      key={contract.id} 
                      to={`/contratos/${contract.id}`}
                      className="block p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{contract.client_name}</p>
                          <p className="text-sm text-slate-500">{contract.origin} → {contract.destination}</p>
                        </div>
                        <Badge>{contract.status}</Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No hay contratos recientes</p>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // ==================== LOGISTICA DASHBOARD ====================
  if (isLogistica && operationalData) {
    const comparisons = operationalData.comparisons || {};
    
    const ComparisonBadge = ({ comparison }) => {
      if (!comparison) return null;
      const { delta, pct } = comparison;
      if (delta === 0 && pct === 0) return null;
      const isPositive = delta > 0;
      return (
        <div className={cn(
          "flex items-center gap-1 text-xs font-medium mt-1",
          isPositive ? "text-emerald-600" : "text-red-600"
        )}>
          {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{delta > 0 ? "+" : ""}{delta} ({pct > 0 ? "+" : ""}{pct}%)</span>
        </div>
      );
    };

    return (
      <Layout>
        <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-logistica">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h1 className="text-3xl font-heading font-bold text-slate-900">Panel de Operaciones</h1>
                <p className="text-slate-500 mt-1">Estado operativo del dia</p>
              </div>
              <PeriodBadge />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <PeriodSelector />
              <Button variant="outline" onClick={fetchData} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </div>

          {/* Alert Banner */}
          {alerts.length > 0 && (
            <div className="space-y-2" data-testid="alerts-banner-logistica">
              {alerts.filter(a => a.severity === "critical").map((alert, i) => (
                <div key={`crit-${i}`} className="flex items-center gap-3 p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="alert-critical">
                  <div className="p-2 bg-red-200 rounded-full flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">{alert.message}</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      {alert.details?.slice(0, 3).map(d => d.folio).join(", ")}
                      {alert.count > 3 ? ` y ${alert.count - 3} mas...` : ""}
                    </p>
                  </div>
                </div>
              ))}
              {alerts.filter(a => a.severity === "warning").map((alert, i) => (
                <div key={`warn-${i}`} className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="alert-warning">
                  <div className="p-2 bg-amber-200 rounded-full flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">{alert.message}</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {alert.details?.slice(0, 3).map(d => `${d.folio} (falta ${d.missing})`).join(", ")}
                      {alert.count > 3 ? ` y ${alert.count - 3} mas...` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Daily snapshot */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Servicios Hoy</p>
                <p className="text-2xl font-bold text-slate-900">{operationalData.today_services || 0}</p>
              </CardContent>
            </Card>
            <Card className="card-hover border-l-4 border-l-red-500">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Sin Asignar (Rojo)</p>
                <p className="text-2xl font-bold text-red-600">{operationalData.red_contracts || 0}</p>
              </CardContent>
            </Card>
            <Card className="card-hover border-l-4 border-l-yellow-500">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Parcial (Amarillo)</p>
                <p className="text-2xl font-bold text-yellow-600">{operationalData.yellow_contracts || 0}</p>
              </CardContent>
            </Card>
            <Card className="card-hover border-l-4 border-l-green-500">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Listo (Verde)</p>
                <p className="text-2xl font-bold text-green-600">{operationalData.green_contracts || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Operativo del Mes */}
          <div className="mt-6" data-testid="monthly-operations-section-logistica">
            <h3 className="text-base font-heading text-slate-600 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Operativo del Mes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="card-hover bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="log-services-month">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-sky-700">Servicios del Mes</p>
                      <p className="text-2xl font-bold text-sky-700 mt-2">{operationalData.services_month || 0}</p>
                      <ComparisonBadge comparison={comparisons.services} />
                      <p className="text-xs text-sky-600 mt-1">Activos (excl. completados)</p>
                    </div>
                    <div className="p-3 bg-sky-200 rounded-xl">
                      <Calendar className="w-5 h-5 text-sky-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="log-services-assigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-teal-700">Asignados</p>
                      <p className="text-2xl font-bold text-teal-700 mt-2">{operationalData.services_assigned || 0}</p>
                      <p className="text-xs text-teal-600 mt-1">Con vehiculo y chofer</p>
                    </div>
                    <div className="p-3 bg-teal-200 rounded-xl">
                      <UserCheck className="w-5 h-5 text-teal-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (operationalData.services_unassigned || 0) > 0
                  ? "from-amber-50 to-yellow-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="log-services-unassigned">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (operationalData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>Sin Asignacion</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (operationalData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )}>{operationalData.services_unassigned || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Falta vehiculo o chofer</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (operationalData.services_unassigned || 0) > 0 ? "bg-amber-200" : "bg-slate-200"
                    )}>
                      <Truck className={cn(
                        "w-5 h-5",
                        (operationalData.services_unassigned || 0) > 0 ? "text-amber-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={cn(
                "card-hover bg-gradient-to-br",
                (operationalData.services_overdue || 0) > 0
                  ? "from-red-50 to-rose-50"
                  : "from-slate-50 to-gray-50"
              )} data-testid="log-services-overdue">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={cn(
                        "text-sm font-medium",
                        (operationalData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>Vencidos</p>
                      <p className={cn(
                        "text-2xl font-bold mt-2",
                        (operationalData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )}>{operationalData.services_overdue || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Fecha pasada, no completados</p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-xl",
                      (operationalData.services_overdue || 0) > 0 ? "bg-red-200" : "bg-slate-200"
                    )}>
                      <Clock className={cn(
                        "w-5 h-5",
                        (operationalData.services_overdue || 0) > 0 ? "text-red-700" : "text-slate-600"
                      )} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex gap-4">
            <Link to="/calendario" className="flex-1">
              <Card className="card-hover cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Ver Calendario</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/asignacion" className="flex-1">
              <Card className="card-hover cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium">Asignaciones</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Fallback
  return (
    <Layout>
      <div className="p-8 text-center">
        <p className="text-slate-500">Cargando dashboard...</p>
      </div>
    </Layout>
  );
}
